import { LIVEKIT_PUBLIC_URL } from '@/env'
import { t } from '@lingui/core/macro'
import { useCallback, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useConversationLogger } from './useConversationLogger'
import type { DataMessageEvent } from './useLiveKit'
import { useLiveKit } from './useLiveKit'

export type RestartState = {
  activeRestartId: string | null
  hasVoted: boolean
  isRequester: boolean
  requiredVotes: number
  yesCount: number
  noCount: number
}

export const initialRestartState: RestartState = {
  activeRestartId: null,
  hasVoted: false,
  isRequester: false,
  requiredVotes: 0,
  yesCount: 0,
  noCount: 0
}

export function useSessionLiveKit() {
  const log = useConversationLogger()
  const [livekitToken, setLivekitToken] = useState('')
  const [restart, setRestart] = useState<RestartState>(initialRestartState)
  const [restartPending, setRestartPending] = useState(false)
  const [recordingStartsAt, setRecordingStartsAt] = useState<string | null>(null)
  const recordingStartsAtRef = useRef(recordingStartsAt)
  recordingStartsAtRef.current = recordingStartsAt

  const handleDataMessage = useCallback(
    (event: DataMessageEvent) => {
      switch (event.type) {
        case 'restart:requested':
          log('session', 'Restart requested', event)
          setRestart((prev) => {
            // If our own API response already set this restart, don't overwrite
            if (prev.activeRestartId === event.restartId) return prev
            return {
              activeRestartId: event.restartId,
              hasVoted: false,
              isRequester: false,
              requiredVotes: event.requiredVotes,
              yesCount: 1, // Requester auto-votes yes
              noCount: 0
            }
          })
          break
        case 'restart:voted':
          log('session', `Restart vote: ${event.vote ? 'yes' : 'no'}`, event)
          setRestart((prev) =>
            prev.activeRestartId === event.restartId
              ? { ...prev, yesCount: event.yesCount, noCount: event.noCount }
              : prev
          )
          break
        case 'restart:approved':
          log('session', 'Restart approved', event)
          break
        case 'restart:executed':
          log('session', 'Restart executed — starting countdown', event)
          setRestart(initialRestartState)
          setRestartPending(false)
          if (event.recordingStartsAt) {
            setRecordingStartsAt(event.recordingStartsAt)
          }
          break
        case 'restart:rejected':
          log('session', 'Restart rejected', event)
          setRestart(initialRestartState)
          setRestartPending(false)
          toast.error(t`Restart declined`)
          break
        case 'countdown:start':
          log('session', `Countdown started, recording at ${event.recordingStartsAt}`, event)
          setRecordingStartsAt(event.recordingStartsAt)
          break
      }
    },
    [log]
  )

  const livekit = useLiveKit({
    token: livekitToken,
    serverUrl: livekitToken ? LIVEKIT_PUBLIC_URL : '',
    onDataMessage: handleDataMessage
  })

  return {
    livekit,
    livekitToken,
    setLivekitToken,
    restart,
    setRestart,
    restartPending,
    setRestartPending,
    recordingStartsAt,
    setRecordingStartsAt,
    recordingStartsAtRef
  }
}
